/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { createReducer, type Draft } from '@reduxjs/toolkit';

import Constants from '../../../../constants';
import Utils from '../../../../utils/utils';
import { LIST_ACTION } from '../../actions';
import { mergeDeep, getReferenceTokens } from '../../state-manager';
import { addPageInstructionReferences } from '../../../../page-instructions';
import { reorderMessages } from '../../../../messages';
import TreeManager from '../../../../contextree/treemanager';

import type { GenericReduxState } from './types';

const insertRow = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  referenceList: any[],
  listIndex: any,
  content: any
) => {
  Utils.setIn(draft[context], [...referenceList, listIndex], content);
};

const updateRow = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  referenceList: any[],
  listIndex: any,
  content: any
) => {
  const mergedContent = mergeDeep(Utils.getIn(draft[context], [...referenceList, listIndex], {}), content);
  Utils.setIn(draft[context], [...referenceList, listIndex], mergedContent);
};

const deleteRow = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  referenceList: any[],
  listIndex: any,
  reference: string
) => {
  const referenceListResults = Utils.getIn(draft[context], referenceList, []);
  if (typeof listIndex === 'string') {
    delete referenceListResults[listIndex];
  } else {
    referenceListResults.splice(listIndex, 1);
  }
  Utils.setIn(draft[context], referenceList, referenceListResults);
  TreeManager.deleteListNode(context, [reference], referenceListResults.length);
};

const moveRow = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  referenceList: any[],
  listIndex: any,
  listMoveToIndex: any
) => {
  const existingContent = Utils.getIn(draft[context], [...referenceList, listIndex], {});
  const referenceListResults = Utils.getIn(draft[context], referenceList, []);
  referenceListResults.splice(listIndex, 1);
  referenceListResults.splice(listMoveToIndex, 0, existingContent);
  Utils.setIn(draft[context], referenceList, referenceListResults);
  reorderMessages(draft, context, referenceList?.join('.'), listIndex, listMoveToIndex);
};

const selectDeselectRow = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  referenceList: any[],
  actions: any[]
) => {
  const rows = Utils.getIn(draft[context], referenceList) || [];
  actions.forEach((action) => {
    const { instruction, content, listIndex } = action;
    if (instruction === Constants.LIST_ACTIONS.SELECT) {
      rows[listIndex] = content;
      const fullReference = referenceList?.join('.');
      addPageInstructionReferences(context, fullReference);
    } else {
      delete rows[listIndex];
    }
  });
  Utils.setIn(draft[context], referenceList, rows.filter(Boolean));
};

const deselectAllRows = (draft: Draft<Record<string, unknown>>, context: string, referenceList: any[]) => {
  Utils.setIn(draft[context], referenceList, []);
};

const replacePage = (
  draft: Draft<Record<string, unknown>>,
  context: string,
  referenceList: string[],
  content: Record<string, string>
) => {
  Utils.setIn(draft[context], referenceList, content);
};

const deletePage = (draft: GenericReduxState<string>, context: string, referenceList: string[]) => {
  Utils.setIn(draft[context], referenceList, {});
};

const listReducer = createReducer({}, (builder) => {
  builder.addCase(LIST_ACTION, (draft: Draft<Record<string, any>>, action: any) => {
    const {
      instruction,
      actions,
      context,
      referenceList,
      listIndex,
      groupIndex,
      listMoveToIndex,
      content = {}
    } = action.payload;
    const referenceTokens = getReferenceTokens(referenceList);

    switch (instruction) {
      case Constants.LIST_ACTIONS.ADD:
        insertRow(draft, context, referenceTokens, groupIndex, content);
        break;
      case Constants.LIST_ACTIONS.INSERT:
        insertRow(draft, context, referenceTokens, listIndex, content);
        break;
      case Constants.LIST_ACTIONS.UPDATE:
        updateRow(draft, context, referenceTokens, listIndex ?? groupIndex, content);
        break;
      case Constants.LIST_ACTIONS.DELETE:
        deleteRow(draft, context, referenceTokens, listIndex ?? groupIndex, referenceList);
        break;
      case Constants.LIST_ACTIONS.MOVE:
        moveRow(draft, context, referenceTokens, listIndex, listMoveToIndex);
        break;
      case Constants.LIST_ACTIONS_TYPE.SELECT_DESELECT:
        selectDeselectRow(draft, context, referenceTokens, actions);
        break;
      case Constants.LIST_ACTIONS_TYPE.DESELECT_ALL:
        deselectAllRows(draft, context, referenceTokens);
        break;
      case Constants.LIST_ACTIONS.REPLACE:
        replacePage(draft, context, referenceTokens, content);
        break;
      case Constants.LIST_ACTIONS.DELETEPAGE:
        deletePage(draft, context, referenceTokens);
        break;
      default:
        break;
    }
  });
});

export default listReducer;
